import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { VaultFolder, VaultOrder, VaultSortBy } from '../../types/vault.types';
import { useAppQuery } from '../use-app-query.hook';

// One folder as it is: a search reads the repository routes instead, so a filtered copy of a
// folder never lands in this cache, which the Data Room browser shares.
export const useVaultFolder = ({
  companyId,
  folderId,
  sortBy,
  orderBy,
}: {
  companyId: string;
  folderId: string;
  orderBy?: VaultOrder;
  sortBy?: VaultSortBy;
}) => {
  const queryClient = useQueryClient();

  const {
    data: folder,
    isError,
    isLoading,
    refetch,
  } = useAppQuery<VaultFolder>({
    queryKey: [QueryKey.GET_VAULT_FOLDER, companyId, folderId, { sortBy, orderBy }],
    queryFn: () =>
      ApiService.get(
        {
          endpoint: BackendRoute.COMPANIES,
          routePath: [companyId, 'vault', 'folders', folderId],
        },
        { queryParams: orderBy && sortBy ? { sortBy, orderBy } : {} },
      ),
    enabled: !!companyId && !!folderId,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VAULT_FOLDER] });
  }, [queryClient]);

  return {
    invalidateQuery,
    folder,
    isError,
    isLoading,
    refetch,
  };
};
